import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { SpellBookBackend } from "./backend";


export function getSchoolIconName(schoolName) {
    const n = schoolName.toLowerCase();
    if (n.includes("abiurazione")) return "MagicSchoolIcon_Abjuration";
    if (n.includes("ammaliamento")) return "MagicSchoolIcon_Enchantment";
    if (n.includes("divinazione")) return "MagicSchoolIcon_Divination";
    if (n.includes("evocazione")) return "MagicSchoolIcon_Conjuration";
    if (n.includes("illusione")) return "MagicSchoolIcon_Illusion";
    if (n.includes("invocazione")) return "MagicSchoolIcon_Universal";
    if (n.includes("necromanzia")) return "MagicSchoolIcon_Necromancy";
    if (n.includes("trasmutazione")) return "MagicSchoolIcon_Transmutation";
    if (n.includes("universale")) return "MagicSchoolIcon_Universal";
    return "MagicSchoolIcon_Universal";
}


export function getPageTitle(pagePath) {
    if (pagePath.startsWith("/spells")) return "Incantesimi";
    if (pagePath.startsWith("/classes")) return "Classi";
    if (pagePath.startsWith("/schools")) return "Scuole";
    return "Spell Book";
}


export function getSchoolIconPath(schoolName) {
    const iconName = getSchoolIconName(schoolName);
    return `/icons/${iconName}.png`
}


function getOuterHeightWithMargins(el) {
  if (!el) return 0;
  const rectHeight = el.getBoundingClientRect().height;
  const cs = window.getComputedStyle(el);
  const mt = parseFloat(cs.marginTop || "0") || 0;
  const mb = parseFloat(cs.marginBottom || "0") || 0;
  const pt = parseFloat(cs.paddingTop || "0") || 0;
  const pb = parseFloat(cs.paddingBottom || "0") || 0;
  return rectHeight + mt + mb + pt + pb;
}

export function useElementHeight() {
    const ref = useRef(null);
    const [height, setHeight] = useState(0);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        let rafId = null;
        const update = () => {
            // read once per frame
            rafId = requestAnimationFrame(() => {
                setHeight(getOuterHeightWithMargins(el));
            });
        };

        // Prefer ResizeObserver
        if (typeof ResizeObserver !== "undefined") {
            const ro = new ResizeObserver(() => update());
            ro.observe(el);
            // initial read
            update();
            return () => {
                ro.disconnect();
                if (rafId) cancelAnimationFrame(rafId);
            };
        }

        // Fallback: listen to window resize and MutationObserver
        const onResize = () => update();
        window.addEventListener("resize", onResize);
        const mo = new MutationObserver(() => update());
        mo.observe(el, { childList: true, subtree: true, attributes: true, characterData: true });
        update();

        return () => {
            window.removeEventListener("resize", onResize);
            mo.disconnect();
            if (rafId) cancelAnimationFrame(rafId);
        };
    }, [ref.current]);

    return [ref, height];
}


function composeScrollKey(key) {
    return `scroll:${key}`
}

export function useScrollRestore(ref, key, enabled = true) {
    // Restore as early as possible so there's no visible jump
    useLayoutEffect(() => {
        if (!enabled || !ref.current) return;
        const saved = sessionStorage.getItem(composeScrollKey(key));
        if (saved != null) {
            // Use rAF to ensure content has rendered
            requestAnimationFrame(() => {
                if (ref.current) ref.current.scrollTop = Number(saved);
            });
        }
    }, [ref, key, enabled]);

    useEffect(() => {
        if (!enabled || !ref.current) return;

        const el = ref.current;
        const onScroll = () => {
            // save continuously
            sessionStorage.setItem(composeScrollKey(key), String(el.scrollTop || 0));
        };

        el.addEventListener("scroll", onScroll, { passive: true });
        // Also save when tab is hidden or page is unloaded
        const saveNow = () => sessionStorage.setItem(composeScrollKey(key), String(el.scrollTop || 0));
        window.addEventListener("visibilitychange", saveNow);
        window.addEventListener("pagehide", saveNow);

        return () => {
            el.removeEventListener("scroll", onScroll);
            window.removeEventListener("visibilitychange", saveNow);
            window.removeEventListener("pagehide", saveNow);
        };
    }, [ref, key, enabled]);
}

export function resetScroll(key, asZero=false) {
    if (asZero) {
        sessionStorage.setItem(composeScrollKey(key), String(0));
    }
    else {
        sessionStorage.removeItem(composeScrollKey(key));
    }
}

export function scrollContainerToChild(container, child, offset = 0, smooth = true) {
    if (!container || !child) return;

    const containerRect = container.getBoundingClientRect();
    const childRect = child.getBoundingClientRect();

    const relativeTop = childRect.top - containerRect.top + container.scrollTop;
    container.scrollTo({
        top: (relativeTop - offset),
        behavior: smooth ? "smooth" : "auto",
    });
}


function composeStateKey(key) {
    return `state:${key}`    
}

export function storeState(key, state) {
    sessionStorage.setItem(composeStateKey(key), JSON.stringify(state));
}

export function loadState(key) {
    const s = sessionStorage.getItem(composeStateKey(key));
    if (s) return JSON.parse(s);
    return null;
}

export function eraseState(key) {
    sessionStorage.removeItem(composeStateKey(key));
}


function composeTagsString(tags, useAbbreviations=true) {
    const tagStrings = tags.map(t => {
        if (useAbbreviations && t.abbreviation) return t.abbreviation;
        return t.name;
    });
    return tagStrings.join(", ");
}

function addTagsAndSeeTextToSpellHeaderEntry(entryValue, tags, seeText, useAbbreviations=true) {
    let s = "";
    const hasTags = tags && tags.length > 0;
    const hasSeeText = seeText === true;
    if (entryValue && entryValue.length > 0) {
        s = entryValue;
        if (hasTags) {
            s += ` (${composeTagsString(tags, useAbbreviations)}`;
            if (hasSeeText) s += ", vedi testo";
            s += ")";
        }
        else if (hasSeeText) {
            s += " (vedi testo)";
        }
        return s;
    }
    else {
        if (hasTags && hasSeeText) {
            s = `Vedi testo (${composeTagsString(tags, useAbbreviations)})`;
        }
        else if (hasTags) {
            s = composeTagsString(tags, useAbbreviations);
        }
        else if (hasSeeText) {
            s = "Vedi testo";
        }
    }
    return s;
}

export function getSpellHeaderEntry_School(spell) {
    let entryValue = spell.school.name;
    if (spell.subSchool) entryValue += ` (${spell.subSchool.name})`
    if (spell.descriptors && spell.descriptors.length > 0) {
        const descs = spell.descriptors.map(d => d.name);
        entryValue += ` [${descs.join(", ")}]`
    }
    
    return {
        title: "Scuola [Descrittori]",
        value: entryValue
    }
}

export function getSpellHeaderEntry_Levels(spell) {
    const levels = spell.casterLevels.map(cl => `${cl.class.name} ${cl.level}`);
    const entryValue = levels.join(", ");
    
    return {
        title: "Livello",
        value: entryValue
    }
}

export function getSpellHeaderEntry_Components(spell) {
    const comps = spell.components.map(c => c.name);
    let entryValue = comps.length > 0 ? comps.join(", ") : null;
    entryValue = addTagsAndSeeTextToSpellHeaderEntry(entryValue, null, spell.componentsSeeText);
    
    return {
        title: "Componenti",
        value: entryValue
    }
}

export function getSpellHeaderEntry_CastingTime(spell) {
    const entryValue = addTagsAndSeeTextToSpellHeaderEntry(spell.castingTime.name, spell.castingTimeTags, spell.castingTimeSeeText);
    
    return {
        title: "Tempo di lancio",
        value: entryValue
    }
}

export function getSpellHeaderEntry_Range(spell) {
    const entryValue = addTagsAndSeeTextToSpellHeaderEntry(spell.range.name, spell.rangeTags, false);
    
    return {
        title: "Raggio di azione",
        value: entryValue
    }
}

export function getSpellHeaderEntry_Aim(spell) {
    const entryValue = addTagsAndSeeTextToSpellHeaderEntry(spell.aim.detail, spell.aimTags, spell.aimSeeText);
    
    return {
        title: spell.aim.title,
        value: entryValue
    }
}

export function getSpellHeaderEntry_Duration(spell) {
    const entryValue = addTagsAndSeeTextToSpellHeaderEntry(spell.duration.name, spell.durationTags, false);
    
    return {
        title: "Durata",
        value: entryValue
    }
}

export function getSpellHeaderEntry_SavingThrow(spell) {
    const entryValue = addTagsAndSeeTextToSpellHeaderEntry(spell.savingThrow.name, spell.savingThrowTags, spell.savingThrowSeeText);
    
    return {
        title: "Tiro Salvezza",
        value: entryValue
    }
}

export function getSpellHeaderEntry_ResistenzaAgliIncantesimi(spell) {
    const entryText = spell.spellResistance === true ? "Sì" : "No";
    const entryValue = addTagsAndSeeTextToSpellHeaderEntry(entryText, spell.spellResistanceTags, spell.spellResistanceSeeText);
    
    return {
        title: "Resistenza agli incantesimi",
        value: entryValue
    }
}

export function getSpellHeaderEntries(spell) {
    return [
        getSpellHeaderEntry_School(spell),
        getSpellHeaderEntry_Levels(spell),
        getSpellHeaderEntry_Components(spell),
        getSpellHeaderEntry_CastingTime(spell),
        getSpellHeaderEntry_Range(spell),
        getSpellHeaderEntry_Aim(spell),
        getSpellHeaderEntry_Duration(spell),
        getSpellHeaderEntry_SavingThrow(spell),
        getSpellHeaderEntry_ResistenzaAgliIncantesimi(spell)
    ]
}


export async function fetchSchools() {
    let schools = await SpellBookBackend.getSchools();
    schools.sort((a, b) => a.name.localeCompare(b.name));
    return schools;
}

export async function fetchSubSchools() {
    let subSchools = await SpellBookBackend.getSubSchools();
    subSchools.sort((a, b) => a.name.localeCompare(b.name));
    return subSchools;
}

export async function fetchClasses() {
    let classes = await SpellBookBackend.getClasses();
    classes.sort((a, b) => a.name.localeCompare(b.name));
    return classes;
}

export async function fetchDescriptors() {
    let descriptors = await SpellBookBackend.getDescriptors();
    descriptors.sort((a, b) => a.name.localeCompare(b.name));
    return descriptors;
}

export async function fetchSpells() {
    let spells = await SpellBookBackend.listSpells();
    spells.sort((a, b) => a.name.localeCompare(b.name));
    return spells;
}
